import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form } from "@unform/web";
import { FormHandles, SubmitHandler } from "@unform/core";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { OptionTypeBase } from "react-select";
import { useSelector, useDispatch, RootStateOrAny } from "react-redux";
import { Input, InputMask, Select } from "components/Shared/Form";
import { statesOptions } from "utils";
import { Scaffold, Footer, UnitCard } from "components/Shared";
import { Banner } from "components/Orofacial/Banner";
import { Treatments } from "components/Orofacial/Treatments";
import { Indications } from "components/Orofacial/Indications";
import {
  BookingIdentUserActions,
  ChkCpfActions,
} from "store/ducks/booking";
import { FetchTagIndexActions } from "store/ducks/unidades";
import {
  GeolocationUnidadeActions,
  ListUnidadesTagsActions,
} from "store/ducks/unidades";
import * as S from "./styles";

const ProspectBookingFlow: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const formRef = useRef<FormHandles>(null);
  const lastCheckedCpf = useRef("");
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [isWaitingForGeocode, setIsWaitingForGeocode] = useState(false);
  const [manualState, setManualState] = useState<OptionTypeBase | null>(null);
  const [manualCity, setManualCity] = useState<OptionTypeBase | null>(null);
  const [manualCityOptions, setManualCityOptions] = useState<OptionTypeBase[]>([]);

  const bookingIdUser = useSelector(
    (state: RootStateOrAny) => state.bookingIdentUser.data[0],
  );
  const geolocation = useSelector(
    (state: RootStateOrAny) => state.geolocationUnidade,
  );
  const { data: listUnitsHof = [], loading: listUnitsLoading } = useSelector<
    RootStateOrAny,
    any
  >((state) => state.listUnidadesTags);

  useEffect(() => {
    dispatch(BookingIdentUserActions.reset());
    dispatch(ListUnidadesTagsActions.request({ tag: "hof" }));
  }, [dispatch]);

   useEffect(() => {
    dispatch(FetchTagIndexActions.request("hof"));
  }, [dispatch]);

  useEffect(() => {
    if (!isWaitingForGeocode || geolocation.loading) return;

    if (geolocation.error) {
      setLocationError(geolocation.error);
      setIsWaitingForGeocode(false);
      setIsLocating(false);
      return;
    }

    setIsWaitingForGeocode(false);
    setIsLocating(false);

    if (!geolocation.data) {
      setLocationError("Não foi possível obter o endereço da sua localização.");
      return;
    }

    const response = geolocation.data;
    const addressComponents = response.results?.[0]?.address_components;
    const cityComponent =
      addressComponents?.find((component: { types: string[] }) =>
        component.types.includes("administrative_area_level_2"),
      ) ||
      addressComponents?.find((component: { types: string[] }) =>
        component.types.includes("locality"),
      );
    const stateComponent = addressComponents?.find(
      (component: { types: string[] }) =>
        component.types.includes("administrative_area_level_1"),
    );
    const city = cityComponent?.long_name?.trim();
    const state = stateComponent?.short_name?.trim();

    if (response.status !== "OK" || !city || !state) {
      setLocationError("Não foi possível identificar a cidade e o estado.");
      return;
    }

    if (!bookingIdUser) {
      setLocationError("Não foi possível encontrar os dados do usuário.");
      return;
    }

    dispatch(
      BookingIdentUserActions.setData([
        {
          ...bookingIdUser,
          cidade: city,
          estado: state,
          idUnidade: null,
        },
      ]),
    );
  }, [bookingIdUser, dispatch, geolocation.data, geolocation.error, geolocation.loading, isWaitingForGeocode]);

  const handleCpfChange = useCallback(
    (cpfValue: string) => {
      const cpf = cpfValue.replace(/\D/g, "");
      if (cpf.length !== 11) {
        lastCheckedCpf.current = "";
        return;
      }

      if (lastCheckedCpf.current === cpf) return;

      lastCheckedCpf.current = cpf;
      dispatch(
        ChkCpfActions.request(
          cpf,
          (nome: string) => {
            if (lastCheckedCpf.current === cpf) {
              formRef.current?.setFieldValue("nome", nome);
            }
          },
          () => {
            if (lastCheckedCpf.current !== cpf) return;

            formRef.current?.setFieldValue("cpf", "");
            formRef.current?.setFieldValue("nome", "");
            lastCheckedCpf.current = "";
          },
        ),
      );
    },
    [dispatch],
  );

  const handleSubmit = useCallback<SubmitHandler>(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          cpf: Yup.string().required("Obrigatório"),
          celular: Yup.string().required("Obrigatório"),
          email: Yup.string().email("E-mail inválido").required("Obrigatório"),
        });

        await schema.validate(data, { abortEarly: false });

        dispatch(
          BookingIdentUserActions.setData([
            {
              nome: data.nome || "",
              cpf: data.cpf,
              celular: data.celular,
              email: data.email,
              cidade: "",
              estado: "",
              idUnidade: null,
              allowLocation: false,
              action: false,
            },
          ]),
        );
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch],
  );

  const handleAllowLocation = useCallback(() => {
    setLocationError(null);

    if (bookingIdUser) {
      dispatch(
        BookingIdentUserActions.setData([
          {
            ...bookingIdUser,
            allowLocation: true,
            action: true,
          },
        ]),
      );
    }

    if (!navigator.geolocation) {
      setLocationError("Este navegador não permite obter a localização.");
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        dispatch(
          GeolocationUnidadeActions.request({
            latlng: `${coords.latitude},${coords.longitude}`,
          }),
        );
        setIsWaitingForGeocode(true);
      },
      (error) => {
        setLocationError(error.message);
        setIsLocating(false);
      },
    );
  }, [bookingIdUser, dispatch]);

  const filteredUnits = listUnitsHof.filter((unit: any) => {
    const hasHofTag = unit?.Tags?.toLowerCase().includes("hof");
    if (!hasHofTag) return false;

    if (!bookingIdUser?.cidade || !bookingIdUser?.estado) return true;

    return (
      unit?.Cidade?.toUpperCase() === bookingIdUser.cidade.toUpperCase() &&
      unit?.UF?.toUpperCase() === bookingIdUser.estado.toUpperCase()
    );
  });

  useEffect(() => {
    if (!manualState) {
      setManualCityOptions([]);
      setManualCity(null);
      return;
    }

    const cities: string[] = Array.from(
      new Set(
        listUnitsHof
          .filter(
            (unit: any) =>
              unit?.UF?.trim().toUpperCase() ===
              manualState.value.toString().toUpperCase(),
          )
          .map((unit: any) => unit?.Cidade?.trim())
          .filter((city: any): city is string => Boolean(city)),
      ),
    ) as string[];

    cities.sort((a, b) => a.localeCompare(b, "pt-BR"));

    setManualCityOptions(
      cities.map((city) => ({
        label: city.toUpperCase(),
        value: city,
      })),
    );
    setManualCity(null);
  }, [listUnitsHof, manualState]);

  const handleManualLocationSubmit = useCallback<SubmitHandler>(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          state: Yup.string().required("Obrigatório"),
          city: Yup.string().required("Obrigatório"),
        });

        await schema.validate(data, { abortEarly: false });

        if (!bookingIdUser) return;

        dispatch(
          BookingIdentUserActions.setData([
            {
              ...bookingIdUser,
              estado: data.state,
              cidade: data.city,
              idUnidade: null,
              allowLocation: false,
              action: true,
            },
          ]),
        );
      } catch (error) {
        console.error(error);
      }
    },
    [bookingIdUser, dispatch],
  );

  const onSelect = useCallback(
    (unit: any) => {
      history.push({
        pathname: `/agendar-avaliacao/${unit.IdUnidade}`,
        state: unit,
      });
    },
    [history],
  );

  if (!bookingIdUser) {
    return (
      <S.Content>
        <S.GroupForm>
          <h2>Agende sua avaliação</h2>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <Input type="hidden" id="nome" name="nome" />
            <InputMask
              placeholder="CPF"
              name="cpf"
              mask="999.999.999-99"
              id="cpf"
              onBlur={(event) => handleCpfChange(event.target.value)}
              isLoading={listUnitsLoading}
            />
            <InputMask
              placeholder="Celular"
              name="celular"
              mask="(99) 99999-9999"
              id="celular"
            />
            <Input name="email" id="email" placeholder="E-mail" />
            <button className="sbmt" type="submit" style={{ marginTop: 16 }}>
              Continuar
            </button>
          </Form>
        </S.GroupForm>
      </S.Content>
    );
  }

  if (bookingIdUser.action !== true) {
    return (
      <S.Content>
        <S.GroupForm>
      <div className="location-permission">
        <h2>Permitir localização automática?</h2>
       <div>
        <button className="permitir"  type="button" onClick={handleAllowLocation}>
          {isLocating ? "Obtendo localização..." : "Permitir"}
        </button>
        
        <button
          className="nao-permitir"
          type="button"
          onClick={() =>
            dispatch(
              BookingIdentUserActions.setData([{ ...bookingIdUser, action: true }]),
            )
          }
        >
          Não permitir
        </button>
        {locationError && <p>{locationError}</p>}
      </div>
      </div>
      </S.GroupForm>
      </S.Content>
    );
  }

  const hasLocation = Boolean(bookingIdUser.cidade && bookingIdUser.estado);
  const cityPlaceholder =
    manualState && manualCityOptions.length === 0 ? "Não há unidades" : "Selecione...";

  if (!hasLocation) {
    return (
      <S.Content>
        <S.GroupForm>
          <Form ref={formRef} onSubmit={handleManualLocationSubmit}>
            <Select
              name="state"
              label="Selecione seu estado:"
              formLabelColor="light"
              options={statesOptions}
              onChange={(option) => setManualState(option)}
              defaultValue={manualState}
            />
            <Select
              name="city"
              formLabelColor="light"
              label="Selecione sua cidade:"
              isDisabled={manualCityOptions.length === 0}
              placeholder={cityPlaceholder}
              options={manualCityOptions}
              onChange={(option) => setManualCity(option)}
              defaultValue={manualCity}
            />
            <button className="sbmt" type="submit">
              Buscar unidades
            </button>
          </Form>
        </S.GroupForm>
      </S.Content>
    );
  }

  return (
   <S.Content>
      <h2>Unidades disponíveis</h2>
      {listUnitsLoading && <p>Buscando unidades...</p>}

      {filteredUnits.length > 0 ? (
        <div style={{ display: "grid", gap: 16 }}>
          {filteredUnits.map((unit: any) => (
            <UnitCard
              key={unit.IdUnidade}
              unit={unit}
              onSelect={onSelect}
              avaliacao={true}
            />
          ))}
        </div>
      ) : (
        <div>
          <p>Nenhuma unidade encontrada para o seu perfil.</p>
          <button type="button" onClick={() => history.push("/agendar-avaliacao")}>
            Voltar
          </button>
        </div>
      )}
    </S.Content>
  );

};

export const Orofacial: React.FC = () => {
  return (
    <Scaffold>
      <S.Container>
        <Banner />
        <Treatments />
        <Indications />
        <ProspectBookingFlow />
      </S.Container>
      <Footer />
    </Scaffold>
  );
};
